import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, inject, input, viewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { disableFormControlsBasedOnStatus, SessionQuery, SessionStore } from '../../../../core/state';
import {
  Cmg1003ApplicationInfoQuery,
  Cmg1003ApplicationInfoStore,
  Cmg1003BorrowerQuery,
  Cmg1003BorrowerStore,
  Cmg1003LookupsQuery,
  Cmg1003StoreQueryService,
} from '../../../../loan-state';
import { IdValue } from '../../../models/id-value.model';
import {
  resetValidations,
  openEmailWindow,
  canUseUnsavedChanges,
  getUniqueId,
  dateWithoutTimeUTC,
  formatDateWithoutMilliseconds,
} from '../../../utils';
import { CmgValidators } from '../../../utils/required.validator';
import { combineLatest, Subject, Subscription } from 'rxjs';
import { debounceTime, filter, map, pairwise, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { BorrowerAddressTypes } from '../../../../cmg-1003/models/borrower-address-types.enum';
import { BorrowerModel } from '../../../../cmg-1003/models/borrower.model';
import { countriesWithSelectOption, states } from '../../../../cmg-1003/models/lookups/common.lookups';
import { Cmg1003SectionTypes } from '../../../../cmg-1003/models/section-types.enum';
import { Cmg1003ConstantsService } from '../../../../cmg-1003/services/cmg-1003-constants.service';
import { CmgUiUtilities } from '../../../../cmg-1003/components/common/cmg-ui-utils';
import { CitizenResidencyEnum } from '../../../../cmg-1003/models';
import { Router } from '@angular/router';
import { APP_ENV } from 'apps/cmg-clear2-ui/src/environments/environment.model';
import { Cmg1003SubSectionTypes } from '../../../../cmg-1003/models/sub-section-types.enum';
import { LoanSummaryComponentActions } from 'apps/cmg-clear2-ui/src/app/loan-summary/components/common/loan-summary-component-actions';
import { Cmg1003Service } from 'apps/cmg-clear2-ui/src/app/cmg-1003/services/cmg-1003.service';

import { InputText } from 'primeng/inputtext';
import { CmgFormControlDomDirective } from '../../../directives/cmg-form-control-dom.directive';
import { CmgValidatorMessagesComponent } from '../../cmg-validator-messages/cmg-validator-messages.component';
import { CmgPrimeNgSsnMaskDirective } from '../../../directives/cmg-ssn-mask.directive';
import { DatePicker } from 'primeng/datepicker';
import { CmgInputErrorDirective } from '../../../directives/cmg-input-error.directive';
import { CmgPrimeNgDatePickerMaskDirective } from '../../../directives/cmg-datepicker-mask.directive';
import { InputMask } from 'primeng/inputmask';
import { CmgPrimeNgInputMaskFocusDirective } from '../../../directives/cmg-input-mask-focus.directive';
import { Tooltip } from 'primeng/tooltip';
import { InputGroup } from 'primeng/inputgroup';
import { InputGroupAddon } from 'primeng/inputgroupaddon';
import { Button } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';
import { QaSlugPipe } from '../../../pipes/qa-slug.pipe';

@Component({
  selector: 'cmg-borrower-details-main',
  templateUrl: './borrower-details-main.component.html',
  styleUrls: ['./borrower-details-main.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: LoanSummaryComponentActions, useExisting: BorrowerDetailsMainComponent }],
  imports: [
    ReactiveFormsModule,
    InputText,
    CmgFormControlDomDirective,
    CmgValidatorMessagesComponent,
    CmgPrimeNgSsnMaskDirective,
    DatePicker,
    CmgInputErrorDirective,
    CmgPrimeNgDatePickerMaskDirective,
    InputMask,
    CmgPrimeNgInputMaskFocusDirective,
    Tooltip,
    InputGroup,
    InputGroupAddon,
    Button,
    Checkbox,
    QaSlugPipe,
  ],
})
export class BorrowerDetailsMainComponent implements OnInit, OnDestroy, LoanSummaryComponentActions {
  private readonly _formBuilder = inject<UntypedFormBuilder>(UntypedFormBuilder);
  private readonly _borrowerQuery = inject(Cmg1003BorrowerQuery);
  private readonly _borrowerStore = inject(Cmg1003BorrowerStore);
  private readonly _applicationStore = inject(Cmg1003ApplicationInfoStore);
  private readonly _applicationQuery = inject(Cmg1003ApplicationInfoQuery);
  private readonly _cmg1003ConstantsService = inject(Cmg1003ConstantsService);
  private readonly _lookupsQuery = inject(Cmg1003LookupsQuery);
  private readonly _sessionQuery = inject(SessionQuery);
  private readonly _sessionStore = inject(SessionStore);
  private readonly _router = inject<Router>(Router);
  private readonly _cdRef = inject(ChangeDetectorRef);
  environment = inject(APP_ENV);
  private readonly _storeQuery = inject(Cmg1003StoreQueryService);
  private readonly _cmg1003Service = inject(Cmg1003Service);

  readonly focusedElement = viewChild('focusedElement', { read: ElementRef });

  @Input() borrower: BorrowerModel;
  /** For QA. The automation ID prefix, formatted as "borrowers-[index]-". */
  readonly autoIdPrefix = input<string>(undefined);
  @Input()
  set hasAdditionalBorrower(hasAdditionalBorrower: boolean) {
    this._hasAdditionalBorrower = hasAdditionalBorrower;
    this.allowAdditionalBorrower = !hasAdditionalBorrower;
    this.onAdditionalBorrowerChanged();
  }

  hasAdditionalBorrowerChanged = false;
  locked = false;
  isSoftLockedStatus = true;
  _hasAdditionalBorrower: boolean;
  forceDisableOnLockKeys = ['equifaxScore', 'transUnionScore', 'experianScore', 'creditScoreMedian'];
  BorrowerAddressTypes = BorrowerAddressTypes;
  borrowerDetailsForm: UntypedFormGroup;
  readonly minDob = this._cmg1003ConstantsService.date200YearsAgo;
  readonly maxDob = this._cmg1003ConstantsService.date18YearsAgo;
  readonly dobAllowedYearsRange = `${this.minDob.getFullYear()}:${this.maxDob.getFullYear()}`;
  readonly maxBorrowerDependents = 100; // Clear 1 limits the # of dependents to 100
  readonly dobDefault = this._cmg1003ConstantsService.date18YearsAgo;
  readonly dateTimePickerCalendarZIndex = this._cmg1003ConstantsService.dateTimePickerCalendarZIndex;
  maritalStatusOptions$ = this._lookupsQuery.select((e) => e.maritalStatusTypes);
  citizenResidencyTypes$ = this._lookupsQuery.select((e) => e.citizenResidencyTypes);
  states = [new IdValue(null, null, 'Select'), ...states];
  countries = countriesWithSelectOption;
  isAdditionalBorrowerPresent = false;
  allowAdditionalBorrower = false;
  formChangesSub: Subscription;
  ngRequiredValidator = Validators.required;
  creditUrl = null;

  /**
   * This subscription should only be managed within `_updateBorrower()`.
   * Internally handles this.borrowerForm.valueChanges, but will be separate from this.formChangesSub.
   */
  updateBorrowerSubscription: Subscription;
  uniqueId = getUniqueId();
  ageFilter = /^[0-9, ]*$/;
  private get _primaryBorrower(): BorrowerModel {
    let primaryBorrower = null;
    // only 1 ever, still calling it with plural b/c return type is array
    const primaryBorrowers = this._borrowerQuery.getAll({
      limitTo: 1,
      filterBy: (entity) => entity.isPrimaryBorrower === true && entity.applicationId === this.borrower.applicationId,
    });

    if (primaryBorrowers.length > 0) {
      primaryBorrower = primaryBorrowers[0];
    }

    return primaryBorrower;
  }
  private _destroyed$ = new Subject();
  private _isClear2DisabledForLoan$ = this._storeQuery.isClear2DisabledForLoan$;

  get isBorrowerEnabled() {
    const enabled =
      this.isSoftLockedStatus && (this.borrower?.isPrimaryBorrower || (!this.borrower?.isPrimaryBorrower && this.allowAdditionalBorrower));
    return enabled;
  }

  getSectionType() {
    return null;
  }

  validate(): void {
    CmgUiUtilities.markFormAsDirty(this.borrowerDetailsForm);
    CmgUiUtilities.markFormAsTouched(this.borrowerDetailsForm);

    if (this.borrowerDetailsForm.invalid) {
      const controlsWithErrors = CmgUiUtilities.getControlsWithErrors(this.borrowerDetailsForm);
      const invalidControls = CmgUiUtilities.getInvalidControlModels(controlsWithErrors);

      const errorPrefix = CmgUiUtilities.getBorrowerNameErrorPrefix(
        this.borrower?.isPrimaryBorrower,
        this.borrower?.firstName,
        this.borrower?.lastName,
        this.borrower?.suffix,
      );

      invalidControls.forEach((t) => {
        switch (t.formControlName) {
          case 'firstName':
            if (t.errors.required) {
              t.invalidMessage = `${errorPrefix} First Name`;
            }
            break;
          case 'lastName':
            if (t.errors.required) {
              t.invalidMessage = `${errorPrefix} Last Name`;
            }
            break;
          case 'ssn':
            if (t.errors.required) {
              t.invalidMessage = `${errorPrefix} Social Security Number`;
            }
            break;
          case 'maritalStatus':
            if (t.errors.required) {
              t.invalidMessage = `${errorPrefix} Marital Status`;
            }
            break;
          case 'dob':
            if (t.errors.required) {
              t.invalidMessage = `${errorPrefix} Date of Birth`;
            }
            break;
          case 'citizenship':
            if (t.errors.required) {
              t.invalidMessage = `${errorPrefix} Citizenship`;
            }
            break;
          case 'dependentsCount':
            if (t.errors.required) {
              t.invalidMessage = `${errorPrefix} Number of Dependents`;
            }
            break;
          case 'dependentAges':
            if (t.errors.cmgValidationError) {
              t.invalidMessage = `${errorPrefix} Age of Dependents`;
            }
            if (t.errors.required) {
              t.invalidMessage = `${errorPrefix} Age of Dependents`;
            }
            break;
          case 'creditScoreMedian':
            if (t.errors.required) {
              t.invalidMessage = `${errorPrefix} Credit must be run on all applications`;
              t.action = () => {
                this._sessionQuery
                  .select((t) => t.fileDataId)
                  .pipe(take(1))
                  .subscribe((fileDataId) => {
                    this._router.navigate([`urla2/${fileDataId}/credit`]);
                  });
              };
            }
            break;
          default:
            break;
        }
      });

      const subSection = this.borrower.isPrimaryBorrower
        ? Cmg1003SubSectionTypes.primaryBorrowerDetails
        : Cmg1003SubSectionTypes.additionalBorrowerDetails;
      this._sessionStore.setValidationError(Cmg1003SectionTypes.borrowerInformation, invalidControls, subSection);

      // This ensures validation styling is applied after everything is checked.
      this._cdRef.detectChanges();
    }
  }

  resetValidations(): void {
    resetValidations(this.borrowerDetailsForm);

    setTimeout(() => {
      this._cdRef.detectChanges();
    }, 100);
  }

  ngOnInit(): void {
    this.isAdditionalBorrowerPresent = this._applicationQuery.isAdditionalBorrowerPresent(this.borrower.applicationId);
    this.borrowerDetailsForm = this._formBuilder.group(
      {
        firstName: ['', Validators.required],
        lastName: ['', Validators.required],
        middleName: [],
        nickName: [],
        suffix: [''],
        ssn: ['', Validators.required],
        dob: new UntypedFormControl(false, {
          validators: [Validators.required],
          updateOn: 'change',
        }),
        homePhone: [],
        mobile: [],
        workPhone: [],
        email: [],
        maritalStatus: ['', [CmgValidators.required(0)]],
        citizenship: [null, CmgValidators.required(0)],
        dependentsCount: [null, [CmgValidators.numberIsGreaterThanOrEqualTo(0)]],
        dependentAges: ['', CmgValidators.commaSeperatedAge()],
        isFirstTimeHomeBuyer: new UntypedFormControl(false, {
          updateOn: 'change',
        }),
        okToPullCredit: new UntypedFormControl(false, {
          updateOn: 'change',
        }),
      },
      { updateOn: 'blur' },
    );
    this._updateBorrower();
    this._awaitAutoRecoveryDecision();

    this._setupContinuousValidation();

    this._storeQuery
      .statusRequiresFullDisabling$()
      .pipe(takeUntil(this._destroyed$))
      .subscribe((locked) => {
        this.locked = locked;
        this._checkToDisableForm();
      });

    this._isClear2DisabledForLoan$.subscribe((isClear2DisabledForLoan) => {
      if (isClear2DisabledForLoan) {
        this.borrowerDetailsForm.disable({ emitEvent: false });
      }
    });

    this._storeQuery
      .statusRequiresDisabling$()
      .pipe(takeUntil(this._destroyed$))
      .subscribe((locked) => {
        this.isSoftLockedStatus = !locked;
      });

    if (!this.isAdditionalBorrowerPresent && !this.borrower?.isPrimaryBorrower) {
      this.allowAdditionalBorrower = true;
      this.onAdditionalBorrowerChanged();
    }

    this.borrowerDetailsForm
      .get('dependentsCount')
      .valueChanges.pipe(takeUntil(this._destroyed$))
      .subscribe((numOfDependents) => {
        this._updateAgeOfDepsValidator(numOfDependents);
      });

    this._storeQuery.isSandboxLoan$.pipe(takeUntil(this._destroyed$)).subscribe((isSandboxLoan) => {
      if (isSandboxLoan) {
        this.borrowerDetailsForm.controls.email.disable({ emitEvent: false });
      }
    });

    this._mapMobileNumberToHomeIfHomeEmpty();
  }

  private _setupContinuousValidation() {
    this._sessionQuery
      .select((s) => s.all1003Loaded)
      .pipe(
        filter((all1003Loaded) => all1003Loaded === true),
        debounceTime(500),
        take(1),
      )
      .subscribe(() => {
        this.borrowerDetailsForm.statusChanges.pipe(debounceTime(0), takeUntil(this._destroyed$)).subscribe(() => {
          this._sessionStore.triggerRevalidation();
        });
      });
  }

  ngOnDestroy(): void {
    this._destroyed$.next(undefined);
    this._destroyed$.complete();
  }

  onAdditionalBorrowerChanged() {
    this.allowAdditionalBorrower = !this.allowAdditionalBorrower;
    this._applicationStore.setAdditionalBorrowerChecked(this.borrower.applicationId, this.allowAdditionalBorrower);
    if (this.allowAdditionalBorrower) {
      if (this.borrowerDetailsForm) {
        this.borrowerDetailsForm.enable({ emitEvent: false });
      }
      this.formChangesSub =
        this.borrowerDetailsForm && this.borrowerDetailsForm.valueChanges.pipe(takeUntil(this._destroyed$)).subscribe(this._onBorrowerFormChanges);
    } else {
      if (this.formChangesSub && !this.formChangesSub.closed) {
        this.formChangesSub.unsubscribe();
      }
      if (this.borrowerDetailsForm && this.hasAdditionalBorrowerChanged) {
        this.borrowerDetailsForm.disable();
      }
    }
    this.hasAdditionalBorrowerChanged = true;
  }

  openEmail() {
    openEmailWindow(this.borrowerDetailsForm.get('email').value);
  }

  private _checkToDisableForm() {
    // TODO - pass formDisabled into disableFormControlsBasedOnStatus as optional parameter so the statusRequiresFullDisabling check
    // does not run twice. We already know the form needs to be disabled here.
    if (this.locked || (!this.borrower.isPrimaryBorrower && !this.isAdditionalBorrowerPresent)) {
      disableFormControlsBasedOnStatus([this.borrowerDetailsForm], this._storeQuery, this.forceDisableOnLockKeys);
      return true;
    }

    return false;
  }

  private readonly _onBorrowerFormChanges = (formValues: UntypedFormGroup) => {
    const updatedBorrower = { ...this.borrower, ...formValues };
    if (updatedBorrower.presentAddress !== null) {
      updatedBorrower.presentAddress = Object.assign(
        {
          addressId: this.borrower.presentAddressId,
        },
        updatedBorrower.presentAddress,
      );
    }

    updatedBorrower.dob = formatDateWithoutMilliseconds(dateWithoutTimeUTC(updatedBorrower.dob));

    this._borrowerStore.update(this.borrower.borrowerId, updatedBorrower);
  };

  private _updateBorrower() {
    if (!this.borrowerDetailsForm) {
      return;
    }

    // Ensures infinite loop (from valueChanges) doesn't occur if borrower was updated by auto-recovery.
    this.updateBorrowerSubscription?.unsubscribe();

    this._sessionQuery
      .select((t) => t.fileDataId)
      .pipe(take(1))
      .subscribe((fileDataId) => (this.creditUrl = `/${fileDataId}/credit`));
    const dontEmitEvent = { emitEvent: false };
    this.borrowerDetailsForm.get('firstName').setValue(this.borrower.firstName, dontEmitEvent);
    this.borrowerDetailsForm.get('lastName').setValue(this.borrower.lastName, dontEmitEvent);
    this.borrowerDetailsForm.get('middleName').setValue(this.borrower.middleName, dontEmitEvent);
    this.borrowerDetailsForm.get('nickName').setValue(this.borrower.nickName, dontEmitEvent);
    this.borrowerDetailsForm.get('suffix').setValue(this.borrower.suffix, dontEmitEvent);
    this.borrowerDetailsForm.get('ssn').setValue(this.borrower.ssn, dontEmitEvent);
    this.borrowerDetailsForm.get('dob').setValue(this.borrower.dob === null ? null : new Date(this.borrower.dob), dontEmitEvent);
    this.borrowerDetailsForm.get('homePhone').setValue(this.borrower.homePhone, dontEmitEvent);
    this.borrowerDetailsForm.get('mobile').setValue(this.borrower.mobile, dontEmitEvent);
    this.borrowerDetailsForm.get('workPhone').setValue(this.borrower.workPhone, dontEmitEvent);
    this.borrowerDetailsForm.get('email').setValue(this.borrower.email, dontEmitEvent);
    this.borrowerDetailsForm.get('maritalStatus').setValue(this.borrower.maritalStatus, dontEmitEvent);
    // When a borrower is converted from Lead->Prospect, citizenship is not defined. Clear 1 sets it to US Citizen
    // and any update on the form saves this value, unless LO changed it. borrower object here is read-only.
    this.borrowerDetailsForm
      .get('citizenship')
      .setValue(this.borrower.citizenship === 0 ? CitizenResidencyEnum.US_Citizen : this.borrower.citizenship, dontEmitEvent);
    this.borrowerDetailsForm.get('dependentsCount').setValue(this.borrower.dependentsCount, dontEmitEvent);
    this.borrowerDetailsForm.get('dependentAges').setValue(this.borrower.dependentAges, dontEmitEvent);

    this.borrowerDetailsForm.get('isFirstTimeHomeBuyer').setValue(this.borrower.isFirstTimeHomeBuyer, dontEmitEvent);
    this.borrowerDetailsForm.get('okToPullCredit').setValue(this.borrower.okToPullCredit, dontEmitEvent);

    this._updateAgeOfDepsValidator(this.borrower.dependentsCount);

    const formDisabled = this._checkToDisableForm();

    if (!formDisabled) {
      this.updateBorrowerSubscription = this.borrowerDetailsForm.valueChanges
        .pipe(
          startWith(this.borrowerDetailsForm.value),
          pairwise(),
          map(([oldValues, newValues]) => {
            const key = Object.keys(newValues).find((k) => newValues[k] !== oldValues[k]);
            return {
              key: key,
              oldValue: oldValues[key],
              newValue: newValues[key],
            };
          }),
          takeUntil(this._destroyed$),
        )
        .subscribe((changeObj) => {
          // all of this to work around reverting to last valid value & unnecessary updates sent by pInputMask when data is invalid.
          let updateStore = true;
          if (changeObj.key) {
            switch (changeObj.key) {
              case 'ssn':
                updateStore = changeObj.newValue === '' || /^\d{9}$/.test(changeObj.newValue);
                break;
              case 'homePhone':
              case 'mobile':
              case 'workPhone':
                updateStore = changeObj.newValue === '' || /^\d{10}$/.test(changeObj.newValue);
                break;
              default:
                break;
            }
            if (updateStore) {
              this._onBorrowerFormChanges(this.borrowerDetailsForm.value);
            } else {
              this.borrowerDetailsForm.get(changeObj.key).setValue(changeObj.oldValue, { emitEvent: false, onlySelf: true });
              this.borrowerDetailsForm.updateValueAndValidity({ onlySelf: true, emitEvent: false });
            }
          }
        });
      //  Focus on primary borrower form element
      if (this.borrower.isPrimaryBorrower) {
        //  setTimeout else focusedElement is undefined
        setTimeout(() => {
          this.focusedElement()?.nativeElement.focus({ preventScroll: true });
        });
      }
    }

    if (!this.borrower.isPrimaryBorrower && !CmgUiUtilities.isIdTemporary(this.borrower.borrowerId)) {
      this.allowAdditionalBorrower = true;
    }
  }

  /**
   * [Auto-Recovery Flow]:
   * 1) Wait for user's decision on auto-recovery modal.
   * 2) User selects "Unsaved Changes" on auto-recovery modal.
   * 3) Borrower store gets updated, via _update1003ForAutoRecovery() in cmg-1003.service.ts.
   * 4) Observable fires (ONCE) to refresh Borrower using latest data.
   */
  private _awaitAutoRecoveryDecision() {
    combineLatest([this._sessionQuery.select((session) => session.needsAutoRecoveryDecision), this._borrowerQuery.dirtyChecker2.isDirty$])
      .pipe(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        filter(([needsDecision, isDirty]) => canUseUnsavedChanges(this._sessionStore.getValue(), isDirty)),
        take(1),
        switchMap(() =>
          combineLatest([
            this._borrowerQuery.selectEntity(this.borrower?.borrowerId),
            this._applicationQuery.selectEntity(this.borrower?.applicationId),
          ]).pipe(
            take(1),
            tap(([borrower, application]) => {
              if (this.isAdditionalBorrowerPresent !== application.additionalBorrowerChecked) {
                this.isAdditionalBorrowerPresent = application.additionalBorrowerChecked;
                this.onAdditionalBorrowerChanged();
              }

              this.borrower = borrower;
              this._updateBorrower();
            }),
          ),
        ),
      )
      .subscribe();
  }

  onDependentsCountChange(value: number): void {
    if (value === null || value === undefined) {
      this.borrowerDetailsForm.get('dependentsCount').setValue(0);
    }
  }

  private _updateAgeOfDepsValidator(numOfDeps: number) {
    if (numOfDeps > 0) {
      this.borrowerDetailsForm.get('dependentAges').addValidators(this.ngRequiredValidator);
    } else {
      this.borrowerDetailsForm.get('dependentAges').setValue('');
      this.borrowerDetailsForm.get('dependentAges').removeValidators(this.ngRequiredValidator);
    }
  }

  /**
   * Triggered when Save Loan is clicked.
   * For Primary Borrower only, copy mobile # to home # if home # is incomplete.
   */
  private _mapMobileNumberToHomeIfHomeEmpty() {
    this._cmg1003Service.isSaving1003$.pipe(takeUntil(this._destroyed$)).subscribe(() => {
      if (this.borrower.isPrimaryBorrower && this.borrowerDetailsForm.controls.mobile.value && !this.borrowerDetailsForm.controls.homePhone.value) {
        this.borrowerDetailsForm.controls.homePhone.setValue(this.borrowerDetailsForm.controls.mobile.value, { emitEvent: false });
        const updatedBorrower = {
          ...this.borrower,
          ...{
            homePhone: this.borrowerDetailsForm.controls.homePhone.value,
          },
        };
        this._borrowerStore.update(this.borrower.borrowerId, updatedBorrower);
      }
    });
  }
}
